import React, { useState, useEffect, useRef } from 'react';
import './AnimatedPlaceholder.css';

// Initial strategy generation examples
const GENERATION_EXAMPLES = [
  "Create a momentum trading bot that buys SPY when RSI is below 30 and sells when RSI is above 70",
  "Build a TQQQ strategy that buys when price is above the 200-day moving average and sells when below",
  "Make an options bot that buys Tesla ATM calls every Friday with 1-month expiry and sells a week before expiration",
  "Design a mean reversion strategy that buys QQQ on 3% daily drops and sells on 2% daily gains",
  "Create a pairs trading bot that trades the spread between MSFT and GOOGL when it deviates from the 30-day average"
];

// Refinement suggestions for existing strategies
const REFINEMENT_EXAMPLES = [
  "Change the asset to MSFT instead of the current one",
  "Switch to daily timeframe instead of minute bars",
  "Use the 200-day SMA instead of the current indicator",
  "Add RSI indicator with overbought at 70 and oversold at 30",
  "Implement a stop loss at 2% below entry price",
  "Add position sizing based on ATR volatility",
  "Change to trade only during market hours 9:30 AM to 4:00 PM ET",
  "Use MACD crossover signals instead of moving averages",
  "Add a trailing stop loss that follows price by 1%",
  "Switch to crypto and trade BTC/USD 24/7",
  "Implement dollar cost averaging with weekly buys",
  "Add momentum confirmation using volume indicators",
  "Change leverage to 2x for TQQQ/SQQQ strategies",
  "Use Bollinger Bands for entry and exit signals",
  "Add a maximum position size limit of $10,000"
];

export default function AnimatedPlaceholder({ isVisible, isRefinementMode = false, customExamples = null }) {
  const [currentExampleIndex, setCurrentExampleIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const typingTimeoutRef = useRef(null);
  const previousModeRef = useRef(isRefinementMode);
  const previousCustomExamplesRef = useRef(customExamples);

  // Reset when mode changes
  useEffect(() => {
    if (previousModeRef.current !== isRefinementMode) {
      previousModeRef.current = isRefinementMode;
      setCurrentExampleIndex(0);
      setCurrentText('');
    }
  }, [isRefinementMode]);

  // Reset when customExamples changes
  useEffect(() => {
    if (previousCustomExamplesRef.current !== customExamples) {
      previousCustomExamplesRef.current = customExamples;
      setCurrentExampleIndex(0);
      setCurrentText('');
    }
  }, [customExamples]);

  useEffect(() => {
    if (!isVisible) {
      // Clean up when not visible - but don't reset the index so cycling continues
      setCurrentText('');
      // Don't reset currentExampleIndex here - keep it for when placeholder returns
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      return;
    }

    // Choose the appropriate examples: customExamples > refinement > generation
    const examples = customExamples || (isRefinementMode ? REFINEMENT_EXAMPLES : GENERATION_EXAMPLES);
    const currentExample = examples[currentExampleIndex];
    
    // Safety check - if currentExample is undefined, reset to index 0
    if (!currentExample) {
      console.warn('[AnimatedPlaceholder] currentExample is undefined, resetting to index 0');
      setCurrentExampleIndex(0);
      return;
    }
    
    // Clear any existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    if (currentText.length < currentExample.length) {
      // Still typing - add next character
      typingTimeoutRef.current = setTimeout(() => {
        setCurrentText(prev => {
          if (prev.length < currentExample.length) {
            return prev + currentExample[prev.length];
          }
          return prev;
        });
      }, 50); // Type one character every 50ms
    } else if (currentText.length === currentExample.length) {
      // Finished typing - wait then move to next example
      typingTimeoutRef.current = setTimeout(() => {
        const nextIndex = (currentExampleIndex + 1) % examples.length;
        setCurrentExampleIndex(nextIndex);
        setCurrentText(''); // Clear text for next example
      }, 2000); // Wait 2 seconds after typing completes
    }

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [currentText, currentExampleIndex, isVisible, isRefinementMode, customExamples]);

  if (!isVisible) return null;

  return (
    <div className="animated-placeholder-container">
      <div className="animated-placeholder-text">
        {currentText}
      </div>
    </div>
  );
}